/**
 * Suppression list admin (2026-08-24).
 *
 * Answers the question that previously had no answer: "why isn't this
 * prospect getting my emails?" Until now the list was write-only from
 * the operator's side — recipients suppressed themselves by clicking
 * unsubscribe, the Resend webhook suppressed hard bounces and spam
 * complaints, and nothing could read any of it back.
 *
 * Design note: the SEARCH box is the primary control, not the list.
 * The realistic entry point is a rep saying "Sarah says she never got
 * it" — so a single-address lookup with a definitive yes/no comes
 * first, and browsing the whole list is secondary.
 */
import { useCallback, useEffect, useState } from 'react';

const REASON_LABEL = {
  unsubscribe: 'Unsubscribed',
  bounce: 'Hard bounce',
  complaint: 'Marked as spam',
  manual: 'Added by your team',
};

const REASON_HELP = {
  unsubscribe: 'They clicked the unsubscribe link in one of your emails.',
  bounce: 'The address rejected mail permanently — it usually does not exist.',
  complaint: 'They marked one of your messages as spam. This cannot be undone here.',
  manual: 'Someone on your team added this address by hand.',
};

// Pastel background + dark ink, kept as self-consistent pairs so they
// stay legible in both themes without a token lookup.
const REASON_CHIP = {
  unsubscribe: { bg: '#e0e7ff', fg: '#3730a3' },
  bounce: { bg: '#fef3c7', fg: '#92400e' },
  complaint: { bg: '#fee2e2', fg: '#991b1b' },
  manual: { bg: '#e2e8f0', fg: '#334155' },
};

export default function SuppressionListCard({ apiBase, authToken }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [query, setQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  }), [authToken]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());
      if (reasonFilter) params.set('reason', reasonFilter);
      const r = await fetch(`${apiBase}/api/email-suppressions?${params.toString()}`, { headers: headers() });
      if (r.status === 403) {
        // Permission-gated. Say so rather than showing an empty list,
        // which reads as "nobody is suppressed" — the opposite of the
        // truth and exactly the wrong conclusion.
        throw new Error('You need the "Manage the suppression list" permission to see this.');
      }
      if (!r.ok) throw new Error(`Failed to load (status ${r.status})`);
      const j = await r.json();
      setRows(Array.isArray(j.suppressions) ? j.suppressions : []);
      setCounts(j.counts || null);
      setTotal(Number(j.total) || 0);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, headers, query, reasonFilter]);

  useEffect(() => {
    if (!authToken) return undefined;
    // Debounced so typing an address doesn't fire a request per key.
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [authToken, load]);

  const restore = async (row) => {
    if (busyId) return;
    setBusyId(row.id);
    setError(null);
    setNotice(null);
    try {
      const r = await fetch(`${apiBase}/api/email-suppressions/${row.id}`, {
        method: 'DELETE', headers: headers(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Failed (status ${r.status})`);
      setNotice(`${row.email} can receive email again.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const add = async () => {
    const email = newEmail.trim();
    if (!email || adding) return;
    setAdding(true);
    setError(null);
    setNotice(null);
    try {
      const r = await fetch(`${apiBase}/api/email-suppressions`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ email }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Failed (status ${r.status})`);
      setNewEmail('');
      setNotice(`${email} will no longer receive email from your workspace.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const card = {
    background: 'var(--room-card, #ffffff)',
    border: '1px solid var(--room-border, #e2e8f0)',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  };
  const input = {
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid var(--room-border, #cbd5e1)',
    background: 'var(--room-surface, #ffffff)',
    color: 'var(--room-text-strong, #0f172a)',
    fontSize: 14,
  };

  return (
    <div style={card}>
      <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)' }}>
        Who is not receiving your email
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.6 }}>
        If someone says they never got your message, check here first. Addresses land on this
        list when a recipient unsubscribes, when mail permanently bounces, or when someone
        marks a message as spam. TrailerCast stops sending to them automatically.
      </p>

      {counts && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {Object.entries(counts).map(([reason, n]) => (
            <button
              key={reason}
              type="button"
              onClick={() => setReasonFilter(reasonFilter === reason ? '' : reason)}
              title={REASON_HELP[reason]}
              style={{
                border: reasonFilter === reason
                  ? '1px solid var(--room-text-strong, #0f172a)'
                  : '1px solid var(--room-border, #e2e8f0)',
                background: (REASON_CHIP[reason] || {}).bg || '#e2e8f0',
                color: (REASON_CHIP[reason] || {}).fg || '#334155',
                borderRadius: 999,
                padding: '5px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {REASON_LABEL[reason] || reason}: {n}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search an email address…"
          style={{ ...input, flex: '1 1 260px', minWidth: 0 }}
        />
        {reasonFilter && (
          <button type="button" onClick={() => setReasonFilter('')} style={{ ...input, cursor: 'pointer' }}>
            Clear filter
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ padding: '10px 12px', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: 13, marginBottom: 12 }}>
          {notice}
        </div>
      )}

      {/* The empty state is suppressed when the load FAILED. An error
          plus "Nobody is suppressed" tells someone debugging a delivery
          complaint the opposite of the truth: they cannot see the list,
          which is not the same as the list being empty. Caught by a
          render test, not by reading the code. */}
      {loading ? (
        <p style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>Loading…</p>
      ) : error ? null : rows.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>
          {query || reasonFilter
            ? 'No suppressed addresses match that.'
            : 'Nobody is suppressed. Every address you send to can receive your email.'}
        </p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--room-text-muted, #64748b)' }}>
                  <th style={{ padding: '8px 8px 8px 0', fontWeight: 600 }}>Address</th>
                  <th style={{ padding: 8, fontWeight: 600 }}>Reason</th>
                  <th style={{ padding: 8, fontWeight: 600 }}>Since</th>
                  <th style={{ padding: 8 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} style={{ borderTop: '1px solid var(--room-border, #e2e8f0)' }}>
                    <td style={{ padding: '10px 8px 10px 0', color: 'var(--room-text-strong, #0f172a)', wordBreak: 'break-all' }}>
                      {row.email}
                      {row.scope === 'global' && (
                        <span
                          title="Applies across every sender, because the address itself bounced or reported spam."
                          style={{ marginLeft: 8, fontSize: 11, color: 'var(--room-text-muted, #64748b)' }}
                        >
                          all senders
                        </span>
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      <span
                        title={REASON_HELP[row.reason]}
                        style={{
                          background: (REASON_CHIP[row.reason] || {}).bg || '#e2e8f0',
                          color: (REASON_CHIP[row.reason] || {}).fg || '#334155',
                          borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600,
                        }}
                      >
                        {REASON_LABEL[row.reason] || row.reason}
                      </span>
                    </td>
                    <td style={{ padding: 8, color: 'var(--room-text-muted, #64748b)', whiteSpace: 'nowrap' }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {row.removable ? (
                        <button
                          type="button"
                          onClick={() => restore(row)}
                          disabled={busyId === row.id}
                          style={{ ...input, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}
                        >
                          {busyId === row.id ? 'Restoring…' : 'Restore'}
                        </button>
                      ) : (
                        // Deliberately not a disabled button. A greyed
                        // control reads as "ask someone with more
                        // access"; this is a rule, and no role can
                        // override it.
                        <span
                          title="They marked a message as spam. Sending again risks the sending reputation of every TrailerCast customer."
                          style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}
                        >
                          Permanent
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > rows.length && (
            <p style={{ marginTop: 10, fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
              Showing {rows.length} of {total}. Search to narrow it down.
            </p>
          )}
        </>
      )}

      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--room-border, #e2e8f0)' }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--room-text-body, #334155)' }}>
          Stop sending to an address
        </label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="someone@company.com"
            style={{ ...input, flex: '1 1 260px', minWidth: 0 }}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          />
          <button
            type="button"
            onClick={add}
            disabled={!newEmail.trim() || adding}
            style={{ ...input, cursor: 'pointer', fontWeight: 600 }}
          >
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
          Applies to your workspace only. Use this when someone asks you directly to stop
          emailing them.
        </p>
      </div>
    </div>
  );
}
